import React, { useMemo, useState } from 'react';
import { Card, Form, Input, Button, Table, Space, message, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { stockAdjustmentService } from '@services/operator/stockAdjustmentService';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { useOperatorProductService } from '@hooks/operator/products/useOperatorProductService';
import { useInventoryAttributeDefinitions } from '@hooks/orders/useInventoryAttributeDefinitions';
import MerchantSelect from '@components/operator/MerchantSelect';
import useSmartBack from '@hooks/shared/useSmartBack';
import { PlusOutlined, QuestionCircleFilled } from '@ant-design/icons';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import PageHeader from '@shared/PageHeader';
import InventorySelectorModal from '@components/shared/selectors/InventorySelectorModal';
import AdjustmentItemModal from './components/AdjustmentItemModal';
import StockAdjustmentConceptModal from '@shared/stock_adjustments/StockAdjustmentConceptModal';
import { getStockAdjustmentItemColumns } from './columns';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';

function StockAdjustmentNewContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const { merchantId } = useOperatorData();
  const productService = useOperatorProductService();
  const attributeDefinitions = useInventoryAttributeDefinitions('operator', merchantId);
  const attributeLabelByName = useMemo(
    () => Object.fromEntries(attributeDefinitions.map(def => [def.name, def.label || def.name])),
    [attributeDefinitions],
  );
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // 區分「儲存並新增下一筆」的 loading，避免兩顆按鈕同時轉 spinner
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [items, setItems] = useState([]);

  // 建議單號：呼叫後端產生 Redis 流水號並填入表單
  const handleSuggestName = async () => {
    try {
      setSuggesting(true);
      const res = await stockAdjustmentService.suggestName({ merchantId });
      if (res.success) {
        form.setFieldsValue({ name: res.data.name });
      } else {
        handleApiError(res, { t });
      }
    } catch (error) {
      handleNetworkError(error, { t });
    } finally {
      setSuggesting(false);
    }
  };

  // Modals
  const [selectorVisible, setSelectorVisible] = useState(false);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [conceptModalVisible, setConceptModalVisible] = useState(false);

  const handleSelectInventory = (inventory) => {
    setSelectedInventory(inventory);
    setSelectorVisible(false);
    setItemModalVisible(true);
  };

  const handleConfirmItem = (itemDataOrArray) => {
    const newItems = Array.isArray(itemDataOrArray) ? itemDataOrArray : [itemDataOrArray];
    const now = Date.now();
    setItems(prev => [...prev, ...newItems.map((item, idx) => ({ ...item, tempId: `${now}-${idx}` }))]);
    setItemModalVisible(false);
    setSelectedInventory(null);
  };

  const handleRemoveItem = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const handleNoteChange = (index, value) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, note: value } : item));
  };

  // 核心提交邏輯：驗證並呼叫 service.create
  const submitValues = async () => {
    const values = await form.validateFields();
    if (items.length === 0) {
      message.error(t('shared.stock_adjustment.error.no_items'));
      return { __aborted: true };
    }

    const payload = {
      ...values,
      items: items.map(item => ({
        inventory_id: item.inventory_id,
        shelf_id: item.shelf_id,
        quantity: item.quantity,
        reason_code: item.reason_code,
        note: item.note
      }))
    };

    return await stockAdjustmentService.create(payload);
  };

  // 主要提交：成功後導向該筆記錄的 show 頁
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await submitValues();
      if (response?.__aborted) return;

      if (response.success) {
        message.success(t('common.message.create_success'));
        // 導向該筆記錄的 show 頁，避免返回快取的列表頁看不到新資料
        const recordId = response.data?.id;
        navigate(`/stock_adjustments/${recordId}`, { replace: true });
      } else {
        message.error(response.message || t('common.message.operation_failed'));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // 儲存並新增下一筆：成功後重置表單（包含 items 清單）留在本頁
  const handleSaveAndAddAnother = async () => {
    setSavingAndAdding(true);
    try {
      const response = await submitValues();
      if (response?.__aborted) return;

      if (response.success) {
        message.success(t('common.message.create_success'));
        // 重置 antd Form 欄位
        form.resetFields();
        // 清空表單外的動態 items 狀態
        setItems([]);
      } else {
        message.error(response.message || t('common.message.operation_failed'));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSavingAndAdding(false);
    }
  };

  const columns = getStockAdjustmentItemColumns(t, { onRemove: handleRemoveItem, onNoteChange: handleNoteChange, attributeLabelByName });

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <PageHeader
        items={['stocks', 'stocks.adjustments', { label: t('shared.stock_adjustment.new_title') }]}
        guideButton={
          <Tooltip title={t('concepts.stock_adjustments.tooltip')}>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
              onClick={() => setConceptModalVisible(true)}
              style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
            />
          </Tooltip>
        }
      />

      <Card title={t('shared.stock_adjustment.basic_info')} bordered={false} style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline">
          <Form.Item label={t('models.stock_adjustment.name')}>
            <Space.Compact style={{ width: 320 }}>
              <Form.Item name="name" noStyle>
                <Input placeholder={t('shared.stock_adjustment.name_placeholder')} />
              </Form.Item>
              <Button
                onClick={handleSuggestName}
                loading={suggesting}
                disabled={loading || savingAndAdding}
              >
                {t('shared.stock_adjustment.suggest_name')}
              </Button>
            </Space.Compact>
          </Form.Item>
          <Form.Item label={t('models.stock_adjustment.note')} name="note" style={{ flex: 1 }}>
            <Input.TextArea rows={1} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Card>

      <Card
        title={t('shared.stock_adjustment.items')}
        bordered={false}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setSelectorVisible(true)}
          >
            {t('common.button.add')}
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={items}
          rowKey="tempId"
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: t('shared.stock_adjustment.no_items') }}
        />
      </Card>

      <FloatingActionBarSpacer />
      <FloatingActionBar
        role="operator"
        onCancel={goBack}
        onSubmit={handleSubmit}
        loading={loading}
        disabled={savingAndAdding}
        extraButtons={(
          <Button
            loading={savingAndAdding}
            disabled={loading}
            onClick={handleSaveAndAddAnother}
          >
            {t('common.button.save_and_add_another')}
          </Button>
        )}
        submitText={t('common.button.submit')}
      />

      <InventorySelectorModal
        visible={selectorVisible}
        onCancel={() => setSelectorVisible(false)}
        onSelect={handleSelectInventory}
        merchantId={merchantId}
        productService={productService}
      />

      <AdjustmentItemModal
        visible={itemModalVisible}
        inventory={selectedInventory}
        existingItems={items}
        defaultReasonCode={items.length > 0 ? items[items.length - 1].reason_code : undefined}
        onCancel={() => setItemModalVisible(false)}
        onConfirm={handleConfirmItem}
      />

      <StockAdjustmentConceptModal
        visible={conceptModalVisible}
        onClose={() => setConceptModalVisible(false)}
      />
    </div>
  );
}

export default function StockAdjustmentNew() {
  return (
    <MerchantSelect>
      <StockAdjustmentNewContent />
    </MerchantSelect>
  );
}
